import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CandidateModal = ({ candidate, isOpen, onClose, onScheduleInterview, onAddNote }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !candidate) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'application', label: 'Application', icon: 'FileText' },
    { id: 'activity', label: 'Activity', icon: 'Clock' },
    { id: 'notes', label: 'Notes', icon: 'MessageSquare' }
  ];

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <Image
              src={candidate?.avatar}
              alt={candidate?.avatarAlt}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <h2 className="text-xl font-semibold text-foreground">{candidate?.name}</h2>
              <p className="text-muted-foreground">{candidate?.position}</p>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-1">
                  <Icon name="MapPin" size={14} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{candidate?.location}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Mail" size={14} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{candidate?.email}</span>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(candidate?.score)}`}>
                  {candidate?.score}% Match
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => onScheduleInterview(candidate)}
            >
              Schedule Interview
            </Button>
            <Button
              variant="ghost"
              iconName="X"
              onClick={onClose}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors duration-150 ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Skills */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {candidate?.skills?.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Experience */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Experience</h3>
                <p className="text-muted-foreground">{candidate?.experience}</p>
              </div>

              {/* Education */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Education</h3>
                <p className="text-muted-foreground">{candidate?.education}</p>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Contact Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <span className="text-muted-foreground">{candidate?.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Phone" size={16} className="text-muted-foreground" />
                    <span className="text-muted-foreground">{candidate?.phone}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Linkedin" size={16} className="text-muted-foreground" />
                    <span className="text-muted-foreground">{candidate?.linkedin}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'application' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-foreground mb-3">Application Details</h3>
                <div className="bg-background p-4 rounded-lg border border-border">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <span className="text-sm text-muted-foreground">Applied Date</span>
                      <p className="font-medium text-foreground">{formatDate(candidate?.appliedDate)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Source</span>
                      <p className="font-medium text-foreground">{candidate?.source}</p>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Cover Letter</span>
                    <p className="text-muted-foreground mt-1">{candidate?.coverLetter}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-foreground mb-3">Documents</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
                    <div className="flex items-center space-x-3">
                      <Icon name="FileText" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">Resume.pdf</p>
                        <p className="text-sm text-muted-foreground">2.3 MB</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" iconName="Download" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
                    <div className="flex items-center space-x-3">
                      <Icon name="FileText" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">Portfolio.pdf</p>
                        <p className="text-sm text-muted-foreground">5.1 MB</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" iconName="Download" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Recent Activity</h3>
              <div className="space-y-4">
                {candidate?.activityHistory?.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name={activity?.icon} size={14} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{activity?.action}</p>
                      <p className="text-xs text-muted-foreground">{activity?.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-foreground">Notes</h3>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => onAddNote(candidate)}
                >
                  Add Note
                </Button>
              </div>
              <div className="space-y-4">
                {candidate?.notes?.map((note, index) => (
                  <div key={index} className="bg-background p-4 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">{note?.author}</span>
                      <span className="text-xs text-muted-foreground">{note?.timestamp}</span>
                    </div>
                    <p className="text-muted-foreground">{note?.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateModal;